/**
 * Stock status, kept honest across a variant change.
 *
 * Only the Stock status block in Automatic mode reaches this file; Manual mode
 * renders no [data-calmy-stockline] attribute at all, so a merchant's own
 * wording is never overwritten by a script.
 *
 * It reads the SAME two things every other live element on this page reads —
 * the [data-calmy-variants] payload and the [data-calmy-id] hidden input the
 * form actually submits — so it can never describe a variant other than the
 * one that would be added to the cart. Same approach as calmy-urgency.js, and
 * deliberately so: one source of truth, watched one way.
 *
 * It invents nothing. Server-side Liquid has already rendered the correct
 * state for the variant the page loaded with; this only keeps up afterwards.
 * If the payload or the input is missing, the rendered text is left exactly as
 * it is rather than blanked or guessed at.
 */
(function () {
  "use strict";
  if (window.__calmyStockLine) return;
  window.__calmyStockLine = true;

  function init(row) {
    if (row.__calmyStockDone) return;
    row.__calmyStockDone = true;

    var section = row.closest(".shopify-section") || document;
    var idInput = section.querySelector("[data-calmy-id]");
    var dataEl = section.querySelector("[data-calmy-variants]");
    var textEl = row.querySelector("[data-calmy-stocktext]");

    /* No data means no update. The server-rendered line stays. */
    if (!idInput || !dataEl || !textEl) return;

    var payload;
    try {
      payload = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    var variants = (payload && payload.variants) || [];
    if (!variants.length) return;

    var inText = row.getAttribute("data-in") || "";
    var outText = row.getAttribute("data-out") || "";

    function find(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    function update() {
      var v = find(idInput.value);
      /* An id that is not in the payload is a state this file cannot describe,
         so it says nothing rather than something wrong. */
      if (!v) return;

      var out = v.available === false;
      var want = out ? outText : inText;

      /* A merchant who cleared one of the two fields gets the row hidden for
         that state instead of an icon with no words beside it. */
      if (!want) {
        row.hidden = true;
        return;
      }

      row.hidden = false;
      row.classList.toggle("is-out", out);
      if (textEl.textContent !== want) textEl.textContent = want;
    }

    update();

    /* The hidden input's value is written by the page's own variant logic, so
       watching the attribute catches every route into a change — swatch, size,
       bundle tier — without this file needing to know any of them. */
    if (window.MutationObserver) {
      new MutationObserver(update).observe(idInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
    }
    /* Belt and braces for any path that sets .value as a property, which
       fires no mutation record. Deferred a tick so it runs after the variant
       logic that handled the same event. */
    section.addEventListener("change", function () {
      window.setTimeout(update, 0);
    });
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-calmy-stockline]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot();
    });
  } else {
    boot();
  }
  /* Re-running a block in the theme editor replaces the markup, so the new
     node needs binding. The __calmyStockDone flag keeps the old ones idle. */
  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
  });
  document.addEventListener("shopify:block:select", function (e) {
    boot(e.target);
  });
})();

/**
 * Estimated dispatch date, in the READER'S timezone.
 *
 * Liquid rendered this row already, using the store's clock. That is correct
 * for a merchant in the shop's timezone and wrong for everyone else for part
 * of every day: a visitor in Sydney at 09:00 is on tomorrow's date while the
 * store is still on yesterday's, so a server-rendered "today + 6" is a day out
 * for them. This recomputes it from the browser's own calendar.
 *
 * It is a DISPATCH estimate and a display string. Nothing here touches an
 * order, a fulfilment or a shipping rate.
 *
 * CALENDAR DAYS, and the Date constructor does the calendar. new Date(y, m,
 * d + 6) normalises 26 Dec + 6 into 1 Jan of the next year on its own — no
 * month lengths, no leap-year table, no rollover arithmetic here to get wrong.
 *
 * FORMAT IS FIXED, not toLocaleDateString. The server-rendered fallback is
 * Liquid's "%b %-d" in English, and a locale-aware client format would rewrite
 * "Sep 26" to "26 Sept" on an en-GB phone the instant the script ran — a
 * visible flicker into a different format than the one the merchant sees in
 * the editor. One format, both sides, no jump.
 */
(function () {
  "use strict";
  if (window.__calmyShipDate) return;
  window.__calmyShipDate = true;

  var MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  function format(d) {
    return MONTHS[d.getMonth()] + " " + d.getDate();
  }

  function iso(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return d.getFullYear() + "-" + (m < 10 ? "0" : "") + m + "-" + (day < 10 ? "0" : "") + day;
  }

  function target(row) {
    var days = parseInt(row.getAttribute("data-days"), 10);
    if (!(days >= 0)) days = 6;
    var now = new Date();
    /* Local midnight of today, then add the days. Building from the date parts
       rather than mutating `now` keeps the result on a clean calendar day and
       side-steps the daylight-saving hour that + (days * 86400000) lands on
       twice a year. */
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  }

  function paint(row) {
    var out = row.querySelector("[data-calmy-shipdate-out]");
    if (!out) return;
    var d = target(row);
    var text = format(d);
    if (out.textContent !== text) out.textContent = text;
    if (out.tagName === "TIME") out.setAttribute("datetime", iso(d));
    row.setAttribute("data-local-day", iso(new Date(d.getFullYear(), d.getMonth(), d.getDate())));
  }

  function paintAll() {
    document.querySelectorAll("[data-calmy-shipdate]").forEach(paint);
  }

  /* Wake just after the next local midnight, repaint, and re-arm. setTimeout
     is clamped and drifts on a sleeping device, which is why the two listeners
     below exist as well rather than this being the only trigger. */
  var timer = null;
  function scheduleMidnight() {
    if (timer) window.clearTimeout(timer);
    var now = new Date();
    var next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2);
    var ms = next - now;
    /* A tab left open for days would otherwise hold one enormous timeout that
       browsers do not fire reliably; cap it and re-check. */
    if (ms > 21600000) ms = 21600000;
    timer = window.setTimeout(function () {
      paintAll();
      scheduleMidnight();
    }, ms);
  }

  function boot() {
    if (!document.querySelector("[data-calmy-shipdate]")) return;
    paintAll();
    scheduleMidnight();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* A tab that has been in the background for a day shows a stale date the
     moment it is looked at again. Repaint on the way back in — this is the
     trigger that actually catches the common case, a phone put down overnight
     with the page still open. */
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") { paintAll(); scheduleMidnight(); }
  });
  /* Back/forward cache restores the DOM as it was, timers and all. */
  window.addEventListener("pageshow", function (e) { if (e.persisted) { paintAll(); scheduleMidnight(); } });
  document.addEventListener("shopify:section:load", function () { boot(); });
})();
